import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../shared/auth";
import LoginModal from "../../../app/LoginModal";
import GoldenEggIcon from "../../../shared/GoldenEggIcon";
import { ordinal } from "../../../online/dynamic";
import { claimGuestRoyaleResult, fetchRoyaleMe, heldGuestRoyaleResult, nextRoyaleReward, royaleReward, setRoyaleTally, ROYALE_REWARDS, type CrownReward, type RoyaleMe } from "../../../shared/royale";
import RewardArt, { rewardTitle } from "./RewardArt";
import s from "./RoyaleReward.module.scss";
import { ON_PORTAL } from "../../../shared/portal-env";

// What a Royale win brought, on the right of the winner's panel (RoyaleEnd):
// which win it was, and the reward it reached, moving (the Royale Laurel on
// its worm, or the eggs), to claim in the Store; with nothing new, how far the
// next reward is. A guest sees the reward the win would bring and signs in to
// keep it. `saved` counts up when the server has stored the result (the tally
// read after that includes this win). `demo` (the design labs): this tally, or
// a guest, and nothing asks the server.
const winsLine = (n: number) => (n === 1 ? "Your first Royale win" : `Your ${ordinal(n)} Royale win`);
const Eggs = ({ n }: { n: number }) => <span className={s.eggs}>+{n}<GoldenEggIcon size={14} /></span>;

function RewardCard({ r, label, action }: { r: CrownReward; label: string; action: React.ReactNode }) {
  return (
    <div className={s.card}>
      <RewardArt r={r} className={s.art} />
      <span className={s.text}><small>{label}</small><b>{rewardTitle(r)}</b><Eggs n={r.eggs} /></span>
      {action}
    </div>
  );
}

function NextUp({ wins }: { wins: number }) {
  const n = nextRoyaleReward(wins);
  if (!n) return null;
  const prev = [...ROYALE_REWARDS].reverse().find((r) => r.wins <= wins);
  const from = prev ? prev.wins : 0;
  const pct = Math.round(((wins - from) / Math.max(1, n.wins - from)) * 100);
  return (
    <div className={s.next}>
      <span className={s.nextTop}><small>Next reward</small><b>{n.name}</b><Eggs n={n.eggs} /></span>
      <span className={s.bar}><i style={{ width: `${pct}%` }} /></span>
      <span className={s.nextSub}>{wins} / {n.wins} wins</span>
    </div>
  );
}

export default function RoyaleReward({ saved, demo }: { saved: number; demo?: RoyaleMe | "guest" }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const user = demo ? (demo === "guest" ? null : auth.user || ({ id: "demo" } as any)) : auth.user;
  const enabled = demo ? true : auth.enabled;
  const [me, setMe] = useState<RoyaleMe | null>(null);
  const [login, setLogin] = useState(false);
  const tries = useRef(0);

  // The tally, once the result is stored. Asked again a few times: the store
  // lands a moment after the match ends.
  useEffect(() => {
    if (demo) { if (demo !== "guest") setMe(demo); return; }
    if (!user) return;
    let stop = false;
    tries.current = 0;
    const read = async () => {
      if (stop) return;
      if (heldGuestRoyaleResult()) await claimGuestRoyaleResult(); // signed in just now: keep the win
      const r = await fetchRoyaleMe();
      if (stop) return;
      if (r && r.wins > 0) { setMe(r); setRoyaleTally(r); return; }
      if (tries.current++ < 4) setTimeout(read, 1500);
    };
    read();
    return () => { stop = true; };
  }, [user, saved]);

  if (!user) {
    // (a game site's build has no sign-in of ours: the win is just the win there)
    if (!enabled || ON_PORTAL) return <span className={s.kicker}>Royale winner</span>;
    const first = royaleReward("crown_1");
    return (
      <>
        <span className={s.kicker}>Royale winner</span>
        {first && <RewardCard r={first} label="Your reward" action={<button type="button" className={s.claim} onClick={() => setLogin(true)}>Sign in to keep it</button>} />}
        {login && <LoginModal dim reason="Sign in to keep your Royale win." onClose={() => setLogin(false)} />}
      </>
    );
  }
  if (!me) return <span className={s.kicker}>Royale winner</span>;

  // The reward this win reached (the newest of those waiting), claimed in the
  // Store: the item's shelf when it brings one.
  const waiting = me.openable.map(royaleReward).filter(Boolean) as CrownReward[];
  const reached = waiting[waiting.length - 1] || null;
  const toStore = (r: CrownReward) => navigate(r.item && r.item.kind === "head" ? "/shop?tab=heads" : "/shop");
  return (
    <>
      <span className={s.kicker}>{winsLine(me.wins)}</span>
      {reached
        ? <RewardCard r={reached} label={reached.item ? "Unlocked" : "Reward"} action={<button type="button" className={s.claim} onClick={() => toStore(reached)}>Claim in the Store</button>} />
        : <NextUp wins={me.wins} />}
    </>
  );
}
